import {
  CameraPhotoPictureImage,
  PhotoImagePictureGalleryPic,
  ChevronLeft,
  ChevronRight,
} from '@awazevr/design-system.components.icon'
import {
  MediaGallery,
  MEDIA_GALLERY_MODULES_ENUM,
} from '@awazevr/design-system.components.media-gallery'
import { Stack } from '@awazevr/design-system.components.stack'
import { Text } from '@awazevr/design-system.components.text'
import {
  Image,
  ImageCarouselOverlay,
} from '@awazevr/design-system.compositions.image-carousel-overlay'
import { breakpoints } from '@awazevr/design-system.utils.react'
import React, { useState, useEffect } from 'react'

import { Swiper } from 'swiper/types'
import {
  AspectRatio,
  DescriptivePaginationCountContainer,
  HeroContainer,
  StyledComingSoonPlaceholder,
  StyledImage,
  StyledImageWrapper,
  StyledNavigationPreviousButton,
  StyledNavigationNextButton,
  StyledImageCount,
} from './CustomMediaGallery.styles'

type CustomMediaGalleryProps = {
  /**
   * Array of images to be rendered in the hero and gallery. If 1 or 0 images are supplied the component will render placeholder image(s) to fill the required space.
   */
  images: Array<Image>
  /**
   * Translation strings
   */
  translations: {
    imageComingSoon: string
    openGalleryTitle: string
    previousNavigation: string
    nextNavigation: string
  }
  /**
   * Callback to trigger when interacting with the carousel
   */
  onCarouselInteractCallback?: (
    event: 'open' | 'close' | 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
  /**
   * Callback to trigger when the swiper is initialized, exposing the Swiper instance.
   */
  onSwiperInit?: (swiper: Swiper) => void
  /**
   * A unique identifier to distinguish multiple instances of the component on the same page.
   */
  uniqueId: string
}

/**
 * This compositions is a hero component that displays 1 or 2 large images depending on screen size.
 *
 * If more images are provided, clicking the "open image gallery" button launches a lightbox containing a carousel that loops through all the available images.
 */
export const CustomMediaGallery: React.FC<CustomMediaGalleryProps> = ({
  images,
  translations,
  onCarouselInteractCallback,
  onSwiperInit,
  uniqueId,
}: CustomMediaGalleryProps) => {
  const [swiper, setSwiper] = useState<Swiper | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const decoratedOnCarouselInteractCallback = (
    event: 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => {
    onCarouselInteractCallback?.(event, index, nextOrPrevious)
  }

  const decoratedOpenCarouselOnCarouselInteractCallback = (
    event: 'navigate' | 'open' | 'close',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => {
    // Ensure index is a valid number before proceeding for legacy carousel implemented in overlay
    if (index === undefined || !Number.isInteger(index)) {
      return
    }

    const targetIndex = index < images.length ? (index ?? 0) : 0

    setCurrentImageIndex(targetIndex)

    onCarouselInteractCallback?.(event, index, nextOrPrevious)
  }

  const decoratedOnSwiperInit = (swiper: Swiper) => {
    setSwiper(swiper)
    onSwiperInit?.(swiper)
  }

  useEffect(() => {
    if (!swiper) return undefined

    const handleSlideChange = () => {
      setCurrentImageIndex(swiper.realIndex)
    }

    swiper.on('slideChange', handleSlideChange)

    return () => {
      swiper.off('slideChange', handleSlideChange)
    }
  }, [swiper])

  const getGalleryContent = (isCarousel: boolean) => {
    const displayImages = images.map((image, index) => {
      const shouldLazyLoad = index > 1 && isCarousel // Lazy load images after the first two images

      const isFloorplan =
        typeof image.src === 'string'
          ? image.src.includes('floorplan')
          : image?.src?.some((src) => src?.includes('floorplan'))
      return (
        <StyledImageWrapper
          key={`media-gallery-real-image-${image.src[0]}-${index}`}
        >
          <AspectRatio>
            <picture>
              {Array.isArray(image.src) && (
                <>
                  <source
                    srcSet={image.src[3]}
                    media={`(min-width: ${breakpoints[2]}px)`}
                  />
                  <source
                    srcSet={image.src[2]}
                    media={`(min-width: ${breakpoints[1]}px)`}
                  />
                  <source
                    srcSet={image.src[1]}
                    media={`(min-width: ${breakpoints[0]}px)`}
                  />
                </>
              )}

              <StyledImage
                src={typeof image.src === 'string' ? image.src : image.src[0]}
                alt={
                  image.alt ?? image.caption ?? `Property image ${index + 1}`
                }
                comingSoon={false}
                isFloorplan={isFloorplan}
                {...(shouldLazyLoad ? { loading: 'lazy' } : {})}
              />
            </picture>
            {shouldLazyLoad && (
              <div className={`swiper-lazy-preloader-${uniqueId}`} />
            )}
          </AspectRatio>
        </StyledImageWrapper>
      )
    })

    const imagesToDisplay = isCarousel ? 2 : 5
    const numberOfComingSoonImages =
      images.length >= imagesToDisplay ? 0 : imagesToDisplay - images.length

    const comingSoonImages = Array.from(
      { length: numberOfComingSoonImages },
      (_, index) => (
        <StyledImageWrapper
          aria-label="Coming Soon"
          key={`media-gallery-coming-soon-image-${index}`}
        >
          <AspectRatio>
            <StyledComingSoonPlaceholder title={translations.imageComingSoon}>
              <Stack direction="column" align="center" justify="center">
                <CameraPhotoPictureImage
                  size="x-large"
                  iconColour="ColorGrey500"
                />
                {isCarousel && (
                  <Text color="ColorGrey500">
                    {translations.imageComingSoon}
                  </Text>
                )}
              </Stack>
            </StyledComingSoonPlaceholder>
          </AspectRatio>
        </StyledImageWrapper>
      ),
    )

    return [...displayImages, ...comingSoonImages]
  }

  const carouselSlides = getGalleryContent(true)

  const prevButtonClassName = 'mg-prev-btn-' + uniqueId
  const nextButtonClassName = 'mg-next-btn-' + uniqueId
  return (
    <HeroContainer>
      <MediaGallery
        onSwiperInit={decoratedOnSwiperInit}
        onCarouselInteractCallback={decoratedOnCarouselInteractCallback}
        moduleList={[MEDIA_GALLERY_MODULES_ENUM.VIRTUAL]}
        swiperConfig={{
          lazyPreloaderClass: `swiper-lazy-preloader-${uniqueId}`,
          lazyPreloadPrevNext: 2,
          ...(carouselSlides.length > 8
            ? {
                virtual: {
                  enabled: true,
                  slides: carouselSlides.map((slide, index) => ({
                    type: 'swiper-slide',
                    slide,
                    id: `hero-carousel-virtual-slide-${index}`,
                  })),
                },
              }
            : {}),
          loop: true,
          slidesPerView: 1,
          spaceBetween: 0,
          preventInteractionOnTransition: true,
          keyboard: false,
          navigation: {
            enabled: true,
            nextEl: `.${nextButtonClassName}`,
            prevEl: `.${prevButtonClassName}`,
          },
          watchOverflow: true,
        }}
      >
        {carouselSlides}
      </MediaGallery>

      <StyledNavigationPreviousButton
        className={prevButtonClassName}
        aria-label={translations.previousNavigation}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ChevronLeft iconColour="ColorWhiteOpacity100" />
      </StyledNavigationPreviousButton>
      <StyledNavigationNextButton
        className={nextButtonClassName}
        aria-label={translations.nextNavigation}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ChevronRight iconColour="ColorWhiteOpacity100" />
      </StyledNavigationNextButton>
      {images.length > 0 && (
        <>
          <DescriptivePaginationCountContainer shouldDiplayPaginationText>
            <StyledImageCount>
              <PhotoImagePictureGalleryPic
                iconColour="ColorThemeLink"
                size="small"
              />
              {currentImageIndex + 1}/{images.length}
            </StyledImageCount>
          </DescriptivePaginationCountContainer>
          <ImageCarouselOverlay
            initialSlide={currentImageIndex}
            close={() => {
              swiper?.slideToLoop(currentImageIndex, 0, false)
              close()
              onCarouselInteractCallback?.('close')
            }}
            isOpen={false}
            images={images}
            translations={{
              previousNavigation: translations.previousNavigation,
              nextNavigation: translations.nextNavigation,
            }}
            onCarouselInteractCallback={
              decoratedOpenCarouselOnCarouselInteractCallback
            }
          />
        </>
      )}
    </HeroContainer>
  )
}
